<script lang="ts">
	import { onMount } from 'svelte';
	import { ChevronLeft, ChevronRight } from 'lucide-svelte';
	import TreeHeader from '$lib/components/tree/TreeHeader.svelte';

	import TreeControls from '$lib/components/tree/TreeControls.svelte';

	import OrderedProgressionPanel from '$lib/components/tree/OrderedProgressionPanel.svelte';
	import EffectsSummaryPanel from '$lib/components/tree/EffectsSummaryPanel.svelte';
	import TimelineSlider from '$lib/components/tree/TimelineSlider.svelte';
	import { initUnifiedTree, type UnifiedInitResult, type LayoutMode } from '$lib/tree/init-unified';
	import { buildGraph } from '$lib/tree/graph';

	import skillData from '$lib/data/skill-data.json';
	import iconData from '$lib/data/icons.json';
	import type { SkillNode } from '$lib/tree/types';
	import {
		applyTheme,
		getStoredTheme,
		type ResolvedTheme,
		type ThemePreference,
		THEME_STORAGE_KEY
	} from '$lib/theme';

	let container = $state<HTMLDivElement>();
	let svg = $state<SVGSVGElement>();
	let tooltip = $state<HTMLDivElement>();
	let zoomInfo = $state<HTMLDivElement>();

	let controls: UnifiedInitResult;
	let currentLayout: LayoutMode = $state('wiki');
	let isTransitioning = $state(false);
	let selectedNodes: string[] = $state([]);
	let activePanel = $state<'progression' | 'summary'>('progression'); // Toggle between panels
	// Sidebar state - desktop uses boolean, mobile has separate drawer state
	let sidebarCollapsed = $state(false);
	let mobileDrawerOpen = $state(false); // Mobile drawer starts closed (peek state)
	
	// Timeline state
	let timelineStep = $state(0);
	let maxTimelineSteps = $state(0);
	let isPlaying = $state(false);
	let playInterval: ReturnType<typeof setInterval> | null = null;
	let playbackSpeed = $state(1); // 1x, 1.5x, 2x
	let unlockOrder: string[] = $state([]);
	let isTimelineScrubbing = false;
	let selectionHistory: string[][] = $state([[]]);
	let selectionHistoryIndex = $state(0);
	let isApplyingSelectionHistory = false;
	let highlightedNodeId: string | null = $state(null);
	let themePreference: ThemePreference = $state('dark');
	let resolvedTheme: ResolvedTheme = $state('dark');

	const SELECTION_HISTORY_STORAGE_KEY = 'demonic-pacts-selection-history-v1';

	// Build graph data for the panel
	const { nodes } = buildGraph(skillData as SkillNode[]);

	function getUnlockNeighbors(nodeId: string): string[] {
		const node = nodes[nodeId];
		if (!node) return [];

		return Array.from(new Set([
			...node.parents,
			...node.children,
			...(node.linked ?? [])
		]));
	}

	function repairUnlockOrder(nodeIds: string[]): string[] {
		const uniqueNodeIds = Array.from(new Set(nodeIds.filter((id) => nodes[id])));
		if (uniqueNodeIds.length === 0) return [];
		if (!uniqueNodeIds.includes('node1')) return uniqueNodeIds;

		// Check if current order is already valid
		const selectedSet = new Set(uniqueNodeIds);
		const resolved = new Set<string>(['node1']);
		let isValidOrder = true;

		for (const nodeId of uniqueNodeIds) {
			if (nodeId === 'node1') continue;
			const node = nodes[nodeId];
			if (!node) {
				isValidOrder = false;
				break;
			}
			// Check if this node has any resolved neighbor (can be unlocked now)
			const canUnlock = getUnlockNeighbors(nodeId).some((neighborId) => resolved.has(neighborId));
			if (!canUnlock) {
				isValidOrder = false;
				break;
			}
			resolved.add(nodeId);
		}

		// If order is valid, preserve it exactly as provided
		if (isValidOrder) {
			return uniqueNodeIds;
		}

		// Check if set is connected (reachable from node1 through selected nodes)
		const reachable = new Set<string>(['node1']);
		const queue = ['node1'];
		while (queue.length > 0) {
			const currentId = queue.shift()!;
			for (const neighborId of getUnlockNeighbors(currentId)) {
				if (!selectedSet.has(neighborId) || reachable.has(neighborId)) continue;
				reachable.add(neighborId);
				queue.push(neighborId);
			}
		}

		// If disconnected, can't repair - return as-is
		if (reachable.size !== selectedSet.size) {
			return uniqueNodeIds;
		}

		// Rebuild order while preserving original sequence as much as possible
		const result: string[] = ['node1'];
		const resolvedSet = new Set<string>(['node1']);
		const remaining = uniqueNodeIds.filter((id) => id !== 'node1');

		while (remaining.length > 0) {
			// Find the first node in remaining order that can be unlocked
			const nextIndex = remaining.findIndex((nodeId) =>
				getUnlockNeighbors(nodeId).some((neighborId) => resolvedSet.has(neighborId))
			);

			if (nextIndex === -1) {
				// No unlockable node found - append remaining in original order
				return [...result, ...remaining];
			}

			const nodeId = remaining[nextIndex];
			resolvedSet.add(nodeId);
			result.push(nodeId);
			remaining.splice(nextIndex, 1);
		}

		return result;
	}

	function getSelectedNodesFromUrl(): string[] {
		if (typeof window === 'undefined') return [];
		const url = new URL(window.location.href);
		const nParam = url.searchParams.get('n');
		if (!nParam) return [];
		
		const nodeNumbers = nParam.split(/[-,]/).map(s => s.trim()).filter(s => s);
		const rawIds = nodeNumbers.map(num => `node${num}`).filter(id => nodes[id]);
		return repairUnlockOrder(rawIds);
	}

	function normalizeSelection(nodeIds: string[]): string[] {
		return repairUnlockOrder(nodeIds.filter((id) => nodes[id]));
	}

	function selectionsEqual(left: string[], right: string[]): boolean {
		if (left.length !== right.length) return false;
		return left.every((id, index) => id === right[index]);
	}

	function loadSelectionHistory(): { entries: string[][]; index: number } | null {
		if (typeof window === 'undefined') return null;
		const raw = window.sessionStorage.getItem(SELECTION_HISTORY_STORAGE_KEY);
		if (!raw) return null;

		try {
			const parsed = JSON.parse(raw) as { entries?: unknown; index?: unknown };
			if (!Array.isArray(parsed.entries) || typeof parsed.index !== 'number') return null;

			const entries = parsed.entries
				.filter(Array.isArray)
				.map((entry) => normalizeSelection(entry.filter((id): id is string => typeof id === 'string')));
			if (entries.length === 0) return null;

			const index = Math.max(0, Math.min(parsed.index, entries.length - 1));
			return { entries, index };
		} catch {
			return null;
		}
	}

	function persistSelectionHistory() {
		if (typeof window === 'undefined') return;
		window.sessionStorage.setItem(
			SELECTION_HISTORY_STORAGE_KEY,
			JSON.stringify({ entries: selectionHistory, index: selectionHistoryIndex })
		);
	}

	function setSelectionHistory(entries: string[][], index: number) {
		selectionHistory = entries.map((entry) => [...entry]);
		selectionHistoryIndex = index;
		persistSelectionHistory();
	}

	function recordSelectionHistory(nodeIds: string[]) {
		if (isApplyingSelectionHistory) return;

		const normalized = normalizeSelection(nodeIds);
		const current = selectionHistory[selectionHistoryIndex] ?? [];
		if (selectionsEqual(current, normalized)) {
			persistSelectionHistory();
			return;
		}

		const nextEntries = selectionHistory.slice(0, selectionHistoryIndex + 1);
		nextEntries.push(normalized);
		const trimmedEntries = nextEntries.slice(-100);
		setSelectionHistory(trimmedEntries, trimmedEntries.length - 1);
	}

	function applySelectionSnapshot(nodeIds: string[]) {
		if (!controls) return;

		const normalized = normalizeSelection(nodeIds);
		isApplyingSelectionHistory = true;
		try {
			controls.clearAllSelections();
			for (const nodeId of normalized) {
				controls.toggleNodeSelection(nodeId);
			}
		} finally {
			isApplyingSelectionHistory = false;
		}
	}

	function undoSelection() {
		if (selectionHistoryIndex <= 0) return;
		const nextIndex = selectionHistoryIndex - 1;
		selectionHistoryIndex = nextIndex;
		persistSelectionHistory();
		applySelectionSnapshot(selectionHistory[nextIndex] ?? []);
	}

	function redoSelection() {
		if (selectionHistoryIndex >= selectionHistory.length - 1) return;
		const nextIndex = selectionHistoryIndex + 1;
		selectionHistoryIndex = nextIndex;
		persistSelectionHistory();
		applySelectionSnapshot(selectionHistory[nextIndex] ?? []);
	}

	function clearChart() {
		if (!controls) return;
		controls.clearAllSelections();
	}

	const canUndo = $derived(selectionHistoryIndex > 0);
	const canRedo = $derived(selectionHistoryIndex < selectionHistory.length - 1);
	const canClearChart = $derived(selectedNodes.length > 0);

	// Parse view from URL param 'v' (format: ?v=packed or ?v=tree-first or ?v=wiki)
	function getViewFromUrl(): LayoutMode | null {
		if (typeof window === 'undefined') return null;
		const url = new URL(window.location.href);
		const vParam = url.searchParams.get('v');
		if (vParam === 'packed' || vParam === 'tree-first' || vParam === 'wiki') {
			return vParam;
		}
		return null;
	}

	function updateUrlNodes() {
		if (typeof window === 'undefined') return;
		
		const url = new URL(window.location.href);
		
		if (unlockOrder.length === 0) {
			url.searchParams.delete('n');
		} else {
			const nodeNumbers = unlockOrder
				.map(id => {
					const match = id.match(/node(\d+)/);
					return match ? parseInt(match[1], 10) : null;
				})
				.filter((n): n is number => n !== null);
			
			url.searchParams.set('n', nodeNumbers.join('-'));
		}
		
		window.history.replaceState({}, '', url.toString());
	}

	// Update only the view param in URL - preserves nodes and other params
	function updateUrlView(view: LayoutMode) {
		if (typeof window === 'undefined') return;
		
		const url = new URL(window.location.href);
		
		// Always set view param (including for packed)
		url.searchParams.set('v', view);
		
		// Use replaceState to avoid adding to history stack
		window.history.replaceState({}, '', url.toString());
	}

	onMount(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		themePreference = getStoredTheme((key) => window.localStorage.getItem(key));
		resolvedTheme = applyTheme(document.documentElement, themePreference, mediaQuery.matches);

		const handleSystemThemeChange = (event: MediaQueryListEvent) => {
			if (themePreference !== 'system') return;
			resolvedTheme = applyTheme(document.documentElement, themePreference, event.matches);
		};

		mediaQuery.addEventListener('change', handleSystemThemeChange);

		const urlView = getViewFromUrl();
		const initialView = urlView || 'wiki';
		currentLayout = initialView;
		
		controls = initUnifiedTree(container!, svg!, tooltip!, zoomInfo!, initialView);
		
		const urlSelectedNodes = getSelectedNodesFromUrl();
		const storedHistory = loadSelectionHistory();

		controls.setOnSelectionChange((ids: string[]) => {
			selectedNodes = ids;
			
			if (!isTimelineScrubbing) {
				const newNodes = ids.filter(id => !unlockOrder.includes(id));
				const removedNodes = unlockOrder.filter(id => !ids.includes(id));
				if (newNodes.length > 0 || removedNodes.length > 0) {
					unlockOrder = unlockOrder
						.filter(id => ids.includes(id))
						.concat(newNodes);
				}
				maxTimelineSteps = unlockOrder.length;
				updateUrlNodes();
			}
			
			timelineStep = isTimelineScrubbing ? timelineStep : unlockOrder.length;
			recordSelectionHistory(ids);
		});
		
		controls.setOnNodeClick((nodeId: string) => {
			console.log('Node clicked:', nodeId);
		});

		controls.setOnNodeHover((nodeId: string | null) => {
			highlightedNodeId = nodeId;
		});

		// URL params take priority over session storage - if URL has state, use it exclusively
		if (urlSelectedNodes.length > 0) {
			// Clear stored history and use URL state as the new baseline
			const normalized = normalizeSelection(urlSelectedNodes);
			setSelectionHistory([normalized], 0);
			applySelectionSnapshot(normalized);
		} else if (storedHistory && storedHistory.entries.length > 0) {
			// No URL params - fall back to session storage
			const currentEntry = storedHistory.entries[storedHistory.index] ?? [];
			setSelectionHistory(storedHistory.entries, storedHistory.index);
			if (currentEntry.length > 0) {
				applySelectionSnapshot(currentEntry);
			} else {
				persistSelectionHistory();
			}
		} else {
			// No state at all
			setSelectionHistory([[]], 0);
			persistSelectionHistory();
		}

		return () => {
			mediaQuery.removeEventListener('change', handleSystemThemeChange);
		};
	});

	function toggleTheme() {
		if (typeof window === 'undefined') return;
		themePreference = resolvedTheme === 'dark' ? 'light' : 'dark';
		window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
		resolvedTheme = applyTheme(
			document.documentElement,
			themePreference,
			window.matchMedia('(prefers-color-scheme: dark)').matches
		);
	}

	function zoomIn() {
		controls?.zoomIn();
	}

	function zoomOut() {
		controls?.zoomOut();
	}

	function fitToScreen() {
		controls?.fitToScreen();
	}

	async function switchLayout(mode: LayoutMode) {
		if (!controls) {
			console.warn('switchLayout: controls not initialized');
			return;
		}
		if (mode === currentLayout) return;

		isTransitioning = true;
		try {
			console.log('switchLayout:', mode, 'from:', currentLayout);
			currentLayout = mode;
			await controls.switchLayout(mode);
			updateUrlView(mode);
		} catch (err) {
			console.error('switchLayout error:', err);
		} finally {
			isTransitioning = false;
		}
	}

	// Timeline functions
	function handleTimelineStepChange(step: number) {
		timelineStep = step;
		if (unlockOrder.length === 0) return;

		if (step < unlockOrder.length) {
			const visible = new Set(unlockOrder.slice(0, step));
			controls?.setTimelineDimming(visible);
		} else {
			controls?.clearTimelineDimming();
		}
	}

	function togglePlay() {
		if (unlockOrder.length === 0) return;
		
		isPlaying = !isPlaying;
		
		if (isPlaying) {
			if (timelineStep >= unlockOrder.length) {
				timelineStep = 0;
			}
			playInterval = setInterval(() => {
				if (timelineStep < unlockOrder.length) {
					handleTimelineStepChange(timelineStep + 1);
				} else {
					isPlaying = false;
					if (playInterval) {
						clearInterval(playInterval);
						playInterval = null;
					}
				}
			}, 400 / playbackSpeed);
		} else {
			if (playInterval) {
				clearInterval(playInterval);
				playInterval = null;
			}
		}
	}

	// Cleanup play interval on unmount
	$effect(() => {
		return () => {
			if (playInterval) {
				clearInterval(playInterval);
			}
		};
	});
</script>

<div class="page-layout">
	<TreeHeader title="OSRS Demonic Pacts" theme={themePreference} onThemeToggle={toggleTheme} />

	<div class="main-area">

{#if false}
<div class="toolbar">
	<div class="layout-toggle" role="group" aria-label="Layout mode">
		<button 
			class="toggle-btn" 
			class:active={currentLayout === 'wiki'}
			onclick={() => switchLayout('wiki')}
			aria-pressed={currentLayout === 'wiki'}
		>
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
				<rect x="3" y="3" width="18" height="18" rx="2"/>
				<path d="M3 9h18M9 21V9"/>
			</svg>
			<span>Wiki</span>
		</button>
		<button 
			class="toggle-btn" 
			class:active={currentLayout === 'tree-first'}
			onclick={() => switchLayout('tree-first')}
			aria-pressed={currentLayout === 'tree-first'}
		>
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
				<path d="M12 3v18M8 7h8M6 12h12M8 17h8"/>
				<circle cx="12" cy="5" r="2"/>
				<circle cx="8" cy="10" r="2"/>
				<circle cx="16" cy="10" r="2"/>
				<circle cx="8" cy="15" r="2"/>
				<circle cx="16" cy="15" r="2"/>
			</svg>
			<span>Tree</span>
		</button>
		<button 
			class="toggle-btn" 
			class:active={currentLayout === 'packed'}
			onclick={() => switchLayout('packed')}
			aria-pressed={currentLayout === 'packed'}
		>
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
				<rect x="3" y="3" width="7" height="7" rx="1"/>
				<rect x="14" y="3" width="7" height="7" rx="1"/>
				<rect x="14" y="14" width="7" height="7" rx="1"/>
				<rect x="3" y="14" width="7" height="7" rx="1"/>
			</svg>
			<span>Packed</span>
		</button>
	</div>
</div>
{/if}

<div class="container" bind:this={container}>
	<svg id="tree-svg" bind:this={svg}></svg>
	<div class="zoom-indicator" bind:this={zoomInfo}>
		Zoom: 100%
	</div>
	<TreeControls
		onZoomIn={zoomIn}
		onZoomOut={zoomOut}
		onFit={fitToScreen}
		onUndo={undoSelection}
		onRedo={redoSelection}
		onClear={clearChart}
		canUndo={canUndo}
		canRedo={canRedo}
		canClear={canClearChart}
	/>
	<div class="points-indicator" class:over-limit={selectedNodes.length > 40}>
		<span class="points-value">{selectedNodes.length}</span>
		<span class="points-separator">/</span>
		<span class="points-max">40</span>
		<span class="points-label">pacts</span>
		{#if selectedNodes.length > 40}
			<span class="points-warning">⚠️ Over limit</span>
		{/if}
	</div>
</div>

<div class="tooltip" bind:this={tooltip}></div>

<!-- Right sidebar: tabs + panel content -->
<div class="sidebar" class:collapsed={sidebarCollapsed}>
	{#if !sidebarCollapsed}
	<div class="panel-toggle">
		<button 
			class="panel-toggle-btn"
			class:active={activePanel === 'progression'}
			onclick={() => activePanel = 'progression'}
			title="Pacts"
		>
			<span>Pacts</span>
			<span class="tab-count">{selectedNodes.length}</span>
		</button>
		<button 
			class="panel-toggle-btn"
			class:active={activePanel === 'summary'}
			onclick={() => activePanel = 'summary'}
			title="Effects Summary"
		>
			<span>Summary</span>
		</button>
	</div>

	<div class="sidebar-content">
		{#if activePanel === 'progression'}
			<OrderedProgressionPanel
				{selectedNodes}
				{nodes}
				icons={iconData}
				{timelineStep}
				onRemove={(id) => controls?.toggleNodeSelection(id)}
				onClear={() => controls?.clearAllSelections()}
				onAddNode={(id) => controls?.toggleNodeSelection(id)}
				onReorder={(newOrder) => {
					unlockOrder = newOrder;
					updateUrlNodes();
				}}
			onHighlightNodes={(ids) => {
				highlightedNodeId = ids[0] ?? null;
				controls?.highlightByIds(ids);
			}}
			onHighlightPreview={(id, suppressSelection) => {
				highlightedNodeId = id;
				suppressSelection ? (controls as any)?.highlightPreview?.(id, true) : controls?.highlightPreview(id);
			}}
			onClearHighlight={() => {
				highlightedNodeId = null;
				controls?.clearTypeHighlight();
			}}
			/>
		{:else if activePanel === 'summary'}
			<EffectsSummaryPanel
				{selectedNodes}
				{nodes}
				icons={iconData}
				{timelineStep}
			onClear={() => controls?.clearAllSelections()}
			onHighlightNodes={(ids) => {
				highlightedNodeId = ids[0] ?? null;
				controls?.highlightByIds(ids);
			}}
			onClearHighlight={() => {
				highlightedNodeId = null;
				controls?.clearTypeHighlight();
			}}
			/>
		{/if}
		</div>
	{/if}
	</div>

	<!-- Sidebar collapse handle - vertical tab centered on sidebar edge -->
	<button 
		class="sidebar-collapse-handle"
		class:collapsed={sidebarCollapsed}
		onclick={() => {
			sidebarCollapsed = !sidebarCollapsed;
			controls?.clearTypeHighlight();
		}}
		title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
	>
		{#if sidebarCollapsed}
			<ChevronLeft size={18} />
		{:else}
			<ChevronRight size={18} />
		{/if}
	</button>


	<TimelineSlider
		maxSteps={maxTimelineSteps}
		currentStep={timelineStep}
		milestoneNodeIds={unlockOrder}
		{nodes}
		icons={iconData}
		tooltipElement={tooltip}
		isPlaying={isPlaying}
		playbackSpeed={playbackSpeed}
		highlightedNodeId={highlightedNodeId}
		onStepChange={handleTimelineStepChange}
		onTogglePlay={togglePlay}
		onHighlightNode={(id) => {
			highlightedNodeId = id;
			controls?.highlightByIds([id]);
		}}
		onClearHighlight={() => {
			highlightedNodeId = null;
			controls?.clearTypeHighlight();
		}}
		onSpeedChange={(speed) => {
			playbackSpeed = speed;
			// Restart interval if playing
			if (isPlaying) {
				if (playInterval) clearInterval(playInterval);
				if (timelineStep >= unlockOrder.length) {
					timelineStep = 0;
				}
				playInterval = setInterval(() => {
					if (timelineStep < unlockOrder.length) {
						handleTimelineStepChange(timelineStep + 1);
					} else {
						isPlaying = false;
						if (playInterval) { clearInterval(playInterval); playInterval = null; }
					}
				}, 400 / playbackSpeed);
			}
		}}
		sidebarCollapsed={sidebarCollapsed}
	/>
	</div> <!-- /main-area -->
</div> <!-- /page-layout -->

<!-- Mobile Drawer - Only visible on mobile (< 768px) -->
{#if typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches}
	<div class="mobile-drawer" class:open={mobileDrawerOpen}>
		<!-- Drawer Handle / Peek State -->
		<button 
			class="mobile-drawer-handle"
			onclick={() => mobileDrawerOpen = !mobileDrawerOpen}
			aria-label={mobileDrawerOpen ? 'Hide panel' : 'Show panel'}
		>
			<div class="handle-bar"></div>
		</button>

		<!-- Drawer Content -->
		<div class="mobile-drawer-content">
			<div class="mobile-drawer-tabs">
				<button 
					class="mobile-drawer-tab"
					class:active={activePanel === 'progression'}
					onclick={() => activePanel = 'progression'}
				>
					<span>Pacts</span>
					<span class="tab-count">{selectedNodes.length}</span>
				</button>
				<button 
					class="mobile-drawer-tab"
					class:active={activePanel === 'summary'}
					onclick={() => activePanel = 'summary'}
				>
					<span>Summary</span>
				</button>
			</div>

			<div class="mobile-drawer-body">
				{#if activePanel === 'progression'}
					<OrderedProgressionPanel
						{selectedNodes}
						{nodes}
						icons={iconData}
						{timelineStep}
						onRemove={(id) => controls?.toggleNodeSelection(id)}
						onClear={() => controls?.clearAllSelections()}
						onAddNode={(id) => controls?.toggleNodeSelection(id)}
						onReorder={(newOrder) => {
							unlockOrder = newOrder;
							updateUrlNodes();
						}}
						onHighlightNodes={(ids) => {
							highlightedNodeId = ids[0] ?? null;
							controls?.highlightByIds(ids);
						}}
						onHighlightPreview={(id, suppressSelection) => {
							highlightedNodeId = id;
							suppressSelection ? (controls as any)?.highlightPreview?.(id, true) : controls?.highlightPreview(id);
						}}
						onClearHighlight={() => {
							highlightedNodeId = null;
							controls?.clearTypeHighlight();
						}}
					/>
				{:else if activePanel === 'summary'}
					<EffectsSummaryPanel
						{selectedNodes}
						{nodes}
						icons={iconData}
						{timelineStep}
						onClear={() => controls?.clearAllSelections()}
						onHighlightNodes={(ids) => {
							highlightedNodeId = ids[0] ?? null;
							controls?.highlightByIds(ids);
						}}
						onClearHighlight={() => {
							highlightedNodeId = null;
							controls?.clearTypeHighlight();
						}}
					/>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.page-layout {
		display: flex;
		flex-direction: column;
		height: 100vh;
		overflow: hidden;
	}

	.main-area {
		flex: 1;
		position: relative;
		display: flex;
		overflow: hidden;
	}

	.toolbar {
		position: absolute;
		top: 8px;
		left: 12px;
		display: flex;
		align-items: center;
		gap: 10px;
		z-index: 999;
	}

	.toolbar > * {
		pointer-events: auto;
	}

	.layout-toggle {
		display: flex;
		align-items: center;
		background: var(--color-bg-elevated);
		backdrop-filter: blur(12px);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		padding: 3px;
		gap: 1px;
	}

	.toggle-btn {
		display: flex;
		align-items: center;
		gap: 5px;
		padding: 5px 10px;
		background: transparent;
		border: none;
		border-radius: 6px;
		color: var(--color-text-muted);
		font-size: 11px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.toggle-btn:hover:not(:disabled) {
		color: var(--color-text-tertiary);
		background: var(--color-bg-subtle);
	}

	.toggle-btn.active {
		background: var(--color-success-soft);
		color: var(--color-success);
	}

	.toggle-btn.switching {
		opacity: 0.7;
	}

	.toggle-btn svg {
		width: 16px;
		height: 16px;
	}

	.zoom-indicator {
		position: absolute;
		bottom: 88px;
		left: 12px;
		padding: 6px 10px;
		background: var(--color-bg-elevated);
		backdrop-filter: blur(12px);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		font-size: 11px;
		font-weight: 500;
		color: var(--color-text-muted);
		font-variant-numeric: tabular-nums;
	}

	.points-indicator {
		position: absolute;
		top: 8px;
		right: 12px;
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 6px 12px;
		background: var(--color-bg-elevated);
		backdrop-filter: blur(12px);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		font-size: 11px;
		font-weight: 500;
		color: var(--color-text-muted);
		font-variant-numeric: tabular-nums;
	}

	.points-indicator.over-limit {
		background: var(--color-danger-soft);
		border-color: var(--color-danger);
		color: var(--color-danger);
	}

	.points-value {
		font-weight: 700;
		color: var(--color-success);
	}

	.points-indicator.over-limit .points-value {
		color: var(--color-danger);
	}

	.points-separator {
		margin: 0 2px;
	}

	.points-max {
		font-weight: 600;
	}

	.points-label {
		margin-left: 4px;
		text-transform: uppercase;
		font-size: 10px;
		letter-spacing: 0.5px;
	}

	.points-warning {
		margin-left: 8px;
		font-size: 10px;
		font-weight: 600;
	}

	.container {
		flex: 1;
		position: relative;
		overflow: hidden;
		cursor: grab;
		background-color: var(--color-bg-canvas);
		background-image: radial-gradient(circle, var(--color-bg-grid-dot) 1px, transparent 1px);
		background-size: 16px 16px;
		touch-action: none; /* Prevent default browser zoom/pan on mobile */
		user-select: none; /* Prevent text selection while dragging */
		-webkit-user-select: none;
	}

	.container:active {
		cursor: grabbing;
	}

	/* GPU layer for SVG */
	#tree-svg {
		width: 100%;
		height: 100%;
		contain: layout paint;
	}

	/* Optimize main group for transforms - the JS will set will-change: transform during drag */
	:global(#tree-svg > g) {
		transform-origin: 0 0;
	}

	.tooltip {
		position: fixed;
		background: var(--color-bg-overlay);
		border: 1px solid var(--color-border-default);
		border-radius: 12px;
		padding: 16px;
		max-width: 400px;
		font-size: 13px;
		line-height: 1.5;
		pointer-events: none;
		z-index: 1010;
		display: none;
		box-shadow: var(--shadow-lg);
	}

	.sidebar {
		width: var(--sidebar-width, 320px);
		display: flex;
		flex-direction: column;
		background: var(--color-bg-surface);
		border-left: 1px solid var(--color-border-muted);
		overflow: hidden;
		transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-left-color 0.2s ease;
		position: relative;
	}

	.sidebar.collapsed {
		width: 0;
		min-width: 0;
		border-left: none;
	}

	.sidebar-collapse-handle {
		position: absolute;
		right: var(--sidebar-width, 320px);
		top: 50%;
		transform: translateY(-50%);
		width: 28px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		background: var(--color-bg-surface);
		border: 1px solid var(--color-border-muted);
		border-right: none;
		border-radius: 6px 0 0 6px;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: right 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 100;
	}

	.sidebar-collapse-handle:hover {
		color: var(--color-text-tertiary);
		background: var(--color-bg-surface-strong);
		border-color: var(--color-border-strong);
	}

	.sidebar-collapse-handle.collapsed {
		right: 0;
	}

	.sidebar-content {
		flex: 1;
		overflow: hidden;
	}

	.panel-toggle {
		display: flex;
		border-bottom: 1px solid var(--color-border-muted);
		flex-shrink: 0;
	}

	.panel-toggle-btn {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		padding: 12px 8px;
		background: transparent;
		border: none;
		color: var(--color-text-muted);
		font-size: 12px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.15s ease;
		border-bottom: 2px solid transparent;
	}

	.panel-toggle-btn:hover {
		color: var(--color-text-tertiary);
		background: var(--color-bg-subtle);
	}

	.panel-toggle-btn.active {
		color: var(--color-text-secondary);
		border-bottom-color: var(--color-accent-primary);
		background: var(--color-bg-subtle-strong);
	}

	.panel-toggle-btn svg {
		width: 14px;
		height: 14px;
	}

	.tab-count {
		font-size: 10px;
		font-weight: 600;
		padding: 1px 6px;
		border-radius: 4px;
		background: var(--color-accent-primary-soft);
		color: var(--color-accent-primary);
	}

	@media (max-width: 768px) {
		.toolbar {
			top: 90px;
			flex-direction: column;
			gap: 8px;
		}

		.zoom-indicator,
		.points-indicator {
			display: none;
		}

		.toggle-btn span {
			display: none;
		}

		.toggle-btn {
			padding: 8px;
		}

		/* Desktop sidebar completely hidden on mobile */
		.sidebar,
		.sidebar-collapse-handle {
			display: none;
		}

		.container {
			right: 0;
		}

		/* Mobile Drawer Styles */
		.mobile-drawer {
			display: flex;
			flex-direction: column;
			position: fixed;
			left: 0;
			right: 0;
			bottom: 0;
			z-index: 1010; /* Above timeline (1002) */
			pointer-events: none; /* Let clicks pass through when closed */
		}

		/* When open, capture all pointer events */
		.mobile-drawer.open {
			pointer-events: auto;
		}

		/* Drawer Handle - Always visible peek at bottom */
		.mobile-drawer-handle {
			position: fixed;
			left: 0;
			right: 0;
			bottom: 0;
			height: 44px;
		background: var(--color-bg-surface);
		border: none;
		border-top: 1px solid var(--color-border-strong);
			border-radius: 16px 16px 0 0;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			cursor: pointer;
			z-index: 1011;
		box-shadow: var(--shadow-md);
			transition: bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1);
			pointer-events: auto;
		}

		/* When drawer is open, handle moves up to top edge */
		.mobile-drawer.open .mobile-drawer-handle {
			bottom: calc(85vh - 44px);
			border-radius: 0;
			border-top: 1px solid var(--color-border-strong);
			border-bottom: 1px solid var(--color-border-muted);
		}

		.handle-bar {
			width: 40px;
			height: 5px;
			background: var(--color-text-muted);
			border-radius: 3px;
		}

		/* Drawer Content - Slides up when open */
		.mobile-drawer-content {
			position: fixed;
			left: 0;
			right: 0;
			bottom: 0;
			height: 0;
			background: var(--color-bg-surface);
			border-radius: 16px 16px 0 0;
			overflow: hidden;
			transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.3s;
			display: flex;
			flex-direction: column;
			z-index: 1010;
			visibility: hidden;
		}

		.mobile-drawer.open .mobile-drawer-content {
			height: 85vh;
			visibility: visible;
		}

		/* Tabs - only visible when drawer is open */
		.mobile-drawer-tabs {
			display: flex;
			border-bottom: 1px solid var(--color-border-muted);
			flex-shrink: 0;
			padding-top: 44px; /* Space for handle at top when open */
			opacity: 0;
			transition: opacity 0.2s ease 0.1s;
		}

		.mobile-drawer.open .mobile-drawer-tabs {
			opacity: 1;
		}

		.mobile-drawer-tab {
			flex: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 6px;
			padding: 12px 8px;
			background: transparent;
			border: none;
			color: var(--color-text-muted);
			font-size: 13px;
			font-weight: 500;
			cursor: pointer;
			transition: all 0.15s ease;
			border-bottom: 2px solid transparent;
			min-height: 48px;
		}

		.mobile-drawer-tab.active {
			color: var(--color-text-secondary);
			border-bottom-color: var(--color-accent-primary);
			background: var(--color-bg-subtle-strong);
		}

		/* Body - fills remaining space in drawer */
		.mobile-drawer-content {
			display: flex;
			flex-direction: column;
			height: 100%;
		}

		.mobile-drawer-body {
			flex: 1;
			overflow-y: auto;
			min-height: 0; /* Allow flex shrinking */
			opacity: 0;
			transition: opacity 0.2s ease 0.1s;
		}

		.mobile-drawer.open .mobile-drawer-body {
			opacity: 1;
		}

		/* Backdrop when open */
		.mobile-drawer.open::before {
			content: '';
			position: fixed;
			inset: 0;
			background: var(--color-backdrop);
			z-index: -1;
			backdrop-filter: blur(2px);
		}
	}

	/* Hide mobile drawer on desktop */
	@media (min-width: 769px) {
		.mobile-drawer {
			display: none !important;
		}
	}
</style>
